/**
 * Normaliza los parámetros de media (imágenes/videos) para carousels y galleries.
 * 
 * Prioriza las dimensiones reales de Prismic cuando están disponibles,
 * luego usa aspectRatio de fluid (Gatsby Image), y finalmente el aspectRatio
 * del slice como último recurso.
 * 
 * @param {Object} params - Parámetros de normalización
 * @param {number|null|undefined} params.aspectRatio - Aspect ratio del slice (puede ser null)
 * @param {number|null|undefined} params.height - Altura del slice (puede ser null)
 * @param {number|null|undefined} params.spacing - Espaciado entre items (puede ser null)
 * @param {Object|null|undefined} params.image - Objeto de imagen con fluid y/o dimensions
 * @param {Object|null|undefined} params.image.fluid - Objeto fluid de Gatsby Image
 * @param {number|null|undefined} params.image.fluid.aspectRatio - Aspect ratio de fluid
 * @param {Object|null|undefined} params.image.dimensions - Dimensiones de Prismic
 * @param {number|null|undefined} params.image.dimensions.width - Ancho de la imagen
 * @param {number|null|undefined} params.image.dimensions.height - Alto de la imagen
 * 
 * @returns {Object} Objeto normalizado con:
 * @returns {number} effectiveAspectRatio - Aspect ratio efectivo calculado
 * @returns {number} innerWidth - Ancho interno de la imagen
 * @returns {number} outerWidth - Ancho externo (incluyendo spacing)
 * @returns {number} safeSpacing - Spacing validado (mínimo 0)
 * @returns {number} safeHeight - Altura validada (mínimo 100px por defecto)
 */
export function normalizeMedia({
  aspectRatio = null,
  height = null,
  spacing = null,
  image = null,
} = {}) {
  // Validar y normalizar height (mínimo 100px por defecto)
  const safeHeight = typeof height === 'number' && height > 0 ? height : 100;

  // Validar y normalizar spacing (mínimo 0)
  const safeSpacing = typeof spacing === 'number' && spacing >= 0 ? spacing : 0;

  // Calcular effectiveAspectRatio con prioridad:
  // 1. dimensions de Prismic (más preciso)
  // 2. fluid.aspectRatio de Gatsby Image
  // 3. aspectRatio del slice (último recurso)
  let effectiveAspectRatio = null;

  if (image?.dimensions?.width && image?.dimensions?.height) {
    // Prioridad 1: Usar dimensiones reales de Prismic
    const dimWidth = Number(image.dimensions.width);
    const dimHeight = Number(image.dimensions.height);
    if (dimWidth > 0 && dimHeight > 0) {
      effectiveAspectRatio = dimWidth / dimHeight;
    }
  }

  if (!effectiveAspectRatio && image?.fluid?.aspectRatio) {
    // Prioridad 2: Usar aspectRatio de fluid (Gatsby Image)
    const fluidAspectRatio = Number(image.fluid.aspectRatio);
    if (fluidAspectRatio > 0 && !isNaN(fluidAspectRatio)) {
      effectiveAspectRatio = fluidAspectRatio;
    }
  }

  if (!effectiveAspectRatio && aspectRatio !== null && aspectRatio !== undefined) {
    // Prioridad 3: Usar aspectRatio del slice
    const sliceAspectRatio = Number(aspectRatio);
    if (sliceAspectRatio > 0 && !isNaN(sliceAspectRatio)) {
      effectiveAspectRatio = sliceAspectRatio;
    }
  }

  // Fallback final: usar 16/9 si todo falla
  if (!effectiveAspectRatio || effectiveAspectRatio <= 0 || isNaN(effectiveAspectRatio)) {
    effectiveAspectRatio = 16 / 9;
  }

  // Calcular dimensiones
  const innerWidth = effectiveAspectRatio * safeHeight;
  const outerWidth = innerWidth + safeSpacing;

  return {
    effectiveAspectRatio,
    innerWidth,
    outerWidth,
    safeSpacing,
    safeHeight,
  };
}

/**
 * Normaliza los parámetros de un slide individual.
 * Útil cuando cada slide puede tener su propia imagen con dimensiones diferentes.
 * 
 * @param {Object} params - Parámetros del slide
 * @param {Object|null|undefined} params.image - Objeto de imagen del slide
 * @param {number|null|undefined} params.fallbackAspectRatio - Aspect ratio a usar si la imagen no tiene dimensiones
 * @param {number|null|undefined} params.height - Altura del contenedor
 * 
 * @returns {number} effectiveAspectRatio - Aspect ratio efectivo para este slide
 */
export function normalizeSlideAspectRatio({
  image = null,
  fallbackAspectRatio = null,
  height = null,
} = {}) {
  const safeHeight = typeof height === 'number' && height > 0 ? height : 100;

  // Prioridad 1: dimensions de Prismic
  if (image?.dimensions?.width && image?.dimensions?.height) {
    const dimWidth = Number(image.dimensions.width);
    const dimHeight = Number(image.dimensions.height);
    if (dimWidth > 0 && dimHeight > 0) {
      return dimWidth / dimHeight;
    }
  }

  // Prioridad 2: fluid.aspectRatio
  if (image?.fluid?.aspectRatio) {
    const fluidAspectRatio = Number(image.fluid.aspectRatio);
    if (fluidAspectRatio > 0 && !isNaN(fluidAspectRatio)) {
      return fluidAspectRatio;
    }
  }

  // Prioridad 3: fallbackAspectRatio
  if (fallbackAspectRatio !== null && fallbackAspectRatio !== undefined) {
    const fallback = Number(fallbackAspectRatio);
    if (fallback > 0 && !isNaN(fallback)) {
      return fallback;
    }
  }

  // Fallback final
  return 16 / 9;
}

